import { Module } from "vuex";

import * as firebase from "firebase/app";
import "firebase/firestore";
import moment from "moment";
import { ServiceStatus } from "@/enums/ServiceStatus";
import { ServiceTypeEnum } from "@/enums/ServiceTypeEnum";
import { batchUpdate } from "@/utils/batchUpdate";

const additionalServicesModule: Module<any, any> = {
  namespaced: false,
  state: {
    additionalService: [],
    additionalServices: [],
    serviceRequests: [],
    serviceRequest: [],
    serviceRequestApprovals: [],
    serviceRequestFilter: [],
    serviceStatusFilter: [],
    selectedServicesStatus: 'AllActive',
    currentResidentServiceRequests: [],
    additionalServiceCategories: {},
    fundingLevels: [],
    emailTemplate: {},
    currentAdditionalServiceAllEvents: [],
    serviceRequestsDashboard: [],
    residentServicesMonth: [],
    serviceRequestByServiceId: [],
    currentVendors: []
  },

  mutations: {
    setServiceRequests(state: any, payload: []) {
      state.serviceRequests = payload;
    },
    setAdditionalServices(state: any, payload: []) {
      state.additionalServices = payload;
    },
    setAdditionalService(state: any, payload: any) {
      state.additionalService = payload;
    },
    setServiceRequest(state: any, payload: []) {
      state.serviceRequest = payload;
    },
    setServiceRequestApprovals(state: any, payload: []) {
      state.serviceRequestApprovals = payload;
    },
    setServiceRequestFilter(state: any, payload: []) {
      state.serviceRequestFilter = payload;
    },
    setServiceStatusFilter(state: any, payload: []) {
      state.serviceStatusFilter = payload;
    },

    setCurrentResidentServiceRequests(state: any, payload: []) {
      state.currentResidentServiceRequests = payload;

      state.serviceRequests = [];
      state.serviceRequests = payload;
    },
    setAdditionalServiceCategories(state: any, payload: []) {
      state.additionalServiceCategories = payload;
    },
    setFundingLevels(state: any, payload: []) {
      state.fundingLevels = payload;
    },
    setEmailTemplate(state: any, payload) {
      state.emailTemplate = payload;
    },
    setSelectedStatus(state, payload): void {
      state.selectedServicesStatus = payload;
    },
    setCurrentAdditionalServiceAllEvents(state: any, payload: []) {
      state.currentAdditionalServiceAllEvents = payload;
    },
    setServiceRequestsDashboard(state: any, payload: []) {
      state.serviceRequestsDashboard = payload;
    },
    setResidentServices(state: any, payload: []) {
      state.residentServicesMonth = payload;
    },
    setServiceRequestsByServiceId(state: any, payload: []) {
      state.serviceRequestByServiceId = payload;
    },
    setVendors(state: any, payload: []) {
      state.currentVendors = payload;
    }
  },

  actions: {
    async createAdditionalService({ dispatch }, service) {
      service.facilityId = this.state.authModule.currentFacility;
      service.status = "Awaiting Approval";
      service.restrictedResidents = [];
      service.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date());

      if(service.durationHours){
        service.durationHours = parseFloat(service.durationHours)
      }

      return firebase
        .firestore()
        .collection("additionalServices")
        .add({ ...service })
        .then((data) => {
          dispatch("getAdditionalServices");
          return data;
        });
    },

    async completeServiceRequest({ commit, dispatch }, { svs }) {
      const completedByStaffName = this.state.authModule.user?.name;
      const completedByStaffId = this.state.authModule.user?.id;

      if(!svs.serviceItemType) throw Error('serviceItemType is empty')

      await firebase
        .firestore()
        .collection("serviceRequests")
        .doc(svs.id)
        .update({ status: "Completed", done: true, serviceItemType: svs.serviceItemType, completedByStaffName: completedByStaffName, completedByStaffId: completedByStaffId })
        .then(() => {
          dispatch("getAdditionalServiceRequest", svs.id);
          dispatch("getAdditionalServiceApprovals", svs.id);
        });
    },

    async updateAdditionalServiceRequest({ commit, dispatch }, request) {
      await firebase
        .firestore()
        .collection("serviceRequests")
        .doc(request.id)
        .update(request).then(() => {
          dispatch("getAdditionalServices");
        });
    },

    async getAdditionalServiceApprovals({ commit }, id) {
      const addSvs = firebase
        .firestore()
        .collection("additionalServiceRequestApprovals")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("serviceRequestId", "==", id);

      await addSvs.get().then(querySnapshot => {
        const svss: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const ds = x.data();
          ds.id = x.id;
          svss.push(ds);
        });
        commit("setServiceRequestApprovals", svss);
      });
    },

    async getAdditionalServiceRequestApprovalsForSocialProgram(
      { commit },
      { socialProgramId, residentIds }
    ) {
      const query = await firebase
        .firestore()
        .collection("additionalServiceRequestApprovals")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("serviceRequestId", "==", socialProgramId);

      if (residentIds) {
        query.where("residentId", "in", residentIds);
      }

      query.get().then(response => {
        const serviceRequests = response.docs.map(serviceRequest => {
          const data = serviceRequest.data();
          data.id = serviceRequest.id;

          return { ...data };
        });

        commit("setServiceRequestApprovals", serviceRequests);
      });
    },

    async getFundingLevels({ commit }) {
      const fundingLevels = firebase
        .firestore()
        .collection("fundingLevel")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      await fundingLevels.get().then(querySnapshot => {
        const svss: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const ds = x.data();
          ds.id = x.id;
          svss.push(ds);
        });
        commit("setFundingLevels", svss);
      });
    },

    async getAdditionalServices({ commit }, type) {
      let query = firebase
        .firestore()
        .collection("additionalServices")
        .where("facilityId", "==", this.state.authModule.currentFacility);

        if(type){
          query = query.where("type","==",type)
        }

      return await query.get().then(querySnapshot => {
        const svss: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const ds = x.data();
          ds.id = x.id;
          svss.push(ds);
        });
        commit("setAdditionalServices", svss);
        return svss;
      });
    },

    async getAdditionalServiceCategories({ commit }) {
      const guestbookRules = firebase
        .firestore()
        .collection("additionalServiceCategories")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      await guestbookRules.get().then(querySnapshot => {
        const services: Array<any> = [];
        querySnapshot.docs.map(response => {
          const serviceCategories = response.data();
          serviceCategories.id = response.id;
          services.push(serviceCategories);
        });

        commit("setAdditionalServiceCategories", services);
      });
    },

    async upsertAdditionalServiceCategories({ dispatch }, serviceCategory) {
      if (!serviceCategory.id) {
        await dispatch("addAdditionalServiceCategories", serviceCategory)
      }

      firebase
        .firestore()
        .collection("additionalServiceCategories")
        .doc(serviceCategory.id).get()
        .then(async (doc) => {
          if (doc.exists) {
            firebase
              .firestore()
              .collection("additionalServiceCategories")
              .doc(doc.id)
              .update({
                categories: serviceCategory.categories
              })
              .then(() => dispatch("getAdditionalServiceCategories"));
          }
          else {
            await dispatch("addAdditionalServiceCategories", serviceCategory)
          }
        });

    },

    async deleteCategoryFromServices(_, category) {
      await firebase
        .firestore()
        .collection("additionalServices")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("category", "==", category)
        .get()
        .then(async (querySnapshot: any) => {
          if(querySnapshot.docs && querySnapshot.docs.length) {
            for(let i = 0; i < querySnapshot.docs.length; i++) {
              const doc = querySnapshot.docs[i];
              await doc.ref.update({ category: "" });
            }
          }
        })
    },

    async deleteCategoryFromRequests(_, category) {
      await firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("category", "==", category)
        .get()
        .then(async (querySnapshot: any) => {
          if(querySnapshot.docs && querySnapshot.docs.length) {
            for(let i = 0; i < querySnapshot.docs.length; i++) {
              const doc = querySnapshot.docs[i];
              await doc.ref.update({ category: "" });
            }
          }
        })
    },

    async updateAdditionalServicesCategory({ state, dispatch }, { categoryId, newCategory }) {
      const services = state.additionalServices.filter(x => x.categoryId === categoryId);

      const itemsToUpdate = services.map(service => {
        return { docId: service.id, updates: { category: newCategory } }
      });

      if (itemsToUpdate.length) {
        return await batchUpdate('additionalServices', itemsToUpdate).then(() => {
          dispatch('getAdditionalServices')
        });
      }
    },

    async updateServicesRequestsCategory({ state, dispatch }, { categoryId, oldCategory, newCategory }) {
      await firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("category", "==", oldCategory)
        .get()
        .then(async (querySnapshot) => {
          const itemsToUpdate = querySnapshot.docs.map(service => {
            return { docId: service.id, updates: { category: newCategory } }
          });

          if (itemsToUpdate.length) {
            return await batchUpdate('serviceRequests', itemsToUpdate);
          }
        });

      await firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("categoryId", "==", categoryId)
        .get()
        .then(async (querySnapshot) => {
          const itemsToUpdate = querySnapshot.docs.map(service => {
            return { docId: service.id, updates: { category: newCategory } }
          });

          if (itemsToUpdate.length) {
            return await batchUpdate('serviceRequests', itemsToUpdate);
          }
        })
    },

    async updateCategoryOrdersBookings(_, service) {
      await firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("serviceId", "==", service.id)
        .get()
        .then((querySnapshot) => {
          querySnapshot.docs.forEach((doc) => {
            doc.ref.update({
              serviceName: service?.name,
              category: service.category,
              categoryId: service.categoryId,
              description: service.description,
              providerName: service.providerName,
              paymentCost: service.oneOffCost,
            });
          })
        })
    },

    async addAdditionalServiceCategories({ dispatch }, serviceCategory) {
      firebase
        .firestore()
        .collection("additionalServiceCategories")
        .add({
          categories: serviceCategory.categories,
          facilityId: this.state.authModule.currentFacility
        })
        .then(() => dispatch("getAdditionalServiceCategories"));
    },

    async getExistAdditionalService({ commit }, payload) {
      const service = payload;
      let events = [];

      const fromDate = moment()
        .subtract(14, "days")
        .toDate();

      const eventsDocuments = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("invitees", "array-contains", service.id);

      await eventsDocuments.get().then(qs => {
        events = qs.docs.map(event => ({
          id: event.id,
          ...event.data()
        }));
      });

      commit("setAdditionalService", { ...service, events });
    },

    async getAdditionalService({ commit }, id) {

      const addSvsSnap = await firebase.firestore().collection("additionalServices").doc(id).get()

      if(!addSvsSnap.exists){
        commit("setAdditionalService", null);
        return null
      }

      const service = { id: addSvsSnap.id, ...addSvsSnap.data() }
      const fromDate = moment().subtract(14, "days").toDate()

      const eventsSnap = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("invitees", "array-contains", service.id).get()

      const events = eventsSnap.docs.map(event => ({ id: event.id, ...event.data()}))

      const result = { ...service, events }

      commit("setAdditionalService", result)

      return result
    },

     async getAdditionalServiceWithoutRelatedData({ commit }, id) {

      const addSvsSnap = await firebase.firestore().collection("additionalServices").doc(id).get()

      if(!addSvsSnap.exists){
        return null
      }

      return { id: addSvsSnap.id, ...addSvsSnap.data() }
    },

    async getAdditionalServiceEvents({ commit }, additionalServiceId) {


      if(!additionalServiceId){
        throw Error('additionalServiceId is empty')
      }

      const fromDate = moment().subtract(14, "days").toDate()

      const eventsSnap = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("invitees", "array-contains", additionalServiceId).get()

      const events = eventsSnap.docs.map(event => ({ id: event.id, ...event.data()}))

      return events
    },

    async getExistAdditionalServiceRequest({ commit, dispatch }, request) {
      const fromDate = moment()
        .toDate();

      const toDate = moment()
        .add(2, "weeks")
        .toDate();

      const conciergeUpcomingEvents = await firebase
        .firestore()
        .collection("calender")
        .where("conciergeRequestId", "==", request.id)
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">", fromDate)
        .where("start", "<", toDate)
        .get()
        .then(qs => {
          return qs.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        });

      const currentSvs = request.data[0];
      if (currentSvs != null) {
        currentSvs.id = request.id;
        currentSvs.conciergeUpcomingEvents = conciergeUpcomingEvents;
        commit("setServiceRequest", currentSvs);
      }
    },

    async getAdditionalServiceRequest({ commit, dispatch }, id) {

      const fromDate = moment()
        .toDate();

      const toDate = moment()
        .add(2, "weeks")
        .toDate();

      const addSvs = firebase
        .firestore()
        .collection("serviceRequests")
        .doc(id);

      const conciergeUpcomingEvents = await firebase
        .firestore()
        .collection("calender")
        .where("conciergeRequestId", "==", id)
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">", fromDate)
        .where("start", "<", toDate)
        .get()
        .then(qs => {
          return qs.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        });

      await addSvs.get().then(querySnapshot => {
        const currentSvs = querySnapshot.data();
        if (currentSvs != null) {
          currentSvs.id = querySnapshot.id;
          currentSvs.conciergeUpcomingEvents = conciergeUpcomingEvents;
          currentSvs.createdDateTime = moment(
            currentSvs.createdDateTime.seconds * 1000
          ).toDate();
          commit("setServiceRequest", currentSvs);
        }
      });
    },

    async deleteAdditionalService({ dispatch }, {additionalServiceId, office365EventId}) {

      await firebase
        .firestore()
        .collection("additionalServices")
        .doc(additionalServiceId)
        .delete();
        

        if(office365EventId){
          await dispatch('deleteMsEvents', { ids: [office365EventId] }).then(response => {
            if (response.data?.status !== 200) {
                console.error('deleteMsEvents error: ', response.data)
            }});
        }
    },

    async updateAdditionalService({ commit }, svs) {
     try {
        if(svs.durationHours){
          svs.durationHours = parseFloat(svs.durationHours)
        }
        svs.updatedDateTime = firebase.firestore.Timestamp.fromDate(new Date())

        await firebase.firestore().collection("additionalServices").doc(svs.id).update(svs);
        console.log('Service updated successfully')
     } catch (error) {
        console.error(error)
     }
    },

    cacheFilterResults({ commit }, serviceRequestTypes) {
      commit("setServiceRequestFilter", serviceRequestTypes);
    },

    cacheStatusFilterResults({ commit }, serviceStatus) {
      commit("setServiceStatusFilter", serviceStatus);
    },

    async getCurrentAdditionalServiceEvents(
      { commit },
      { additionalServiceId }
    ) {
      const today = moment().toDate();
      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("invitees", "array-contains", additionalServiceId)
        .where("start", ">=", today)
        .where(
          "start",
          "<=",
          moment()
            .add(2, "months")
            .toDate()
        )
        .orderBy("start", "asc");

      return await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          const start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.id = x.id;
          thisRes.start = start;
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          resList.push(thisRes);
        });
        commit("setCurrentAdditionalServiceAllEvents", resList);
      });
    },

    clearCurrentAdditionalService({ commit }) {
      commit("setAdditionalService", null);
    },

    async getServiceRequests({ commit }, status) {
      let requests = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)

      if (status?.length && Object.values(ServiceStatus).includes(status)) {
        requests = requests.where("status", "==", status);
      }

      return await requests.orderBy("createdDateTime", "desc").get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.createdDateTime = moment(
            thisRes.createdDateTime.seconds * 1000
          ).toDate();

          resList.push(thisRes);
        });
        commit("setServiceRequests", resList);
        return resList
      });
    },

    async getAdditionalServiceRequests({ commit }, serviceRequestTypesIds) {
      let statusFilter = this.state.additionalServicesModule.serviceStatusFilter;

      let serviceRequests = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility);
      //.where("done", "==", false); -- is this used?

      if (serviceRequestTypesIds?.length) {
        serviceRequests = serviceRequests.where(
          "serviceId",
          "in",
          serviceRequestTypesIds
        );
      }

      if (statusFilter == "Cancelled") {
        statusFilter = ServiceStatus.Cancelled;
      }

      if (statusFilter?.length && statusFilter !== "All") {
        serviceRequests = serviceRequests.where("status", "==", statusFilter);
      }

      await serviceRequests.get().then(querySnapshot => {
        const resList: Array<unknown> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.createdDateTime = moment(
            thisRes.createdDateTime.seconds * 1000
          ).toDate();
          resList.push(thisRes);
        });

        console.log(resList.length);
        commit("setServiceRequests", resList);
      });
    },

    async getAdditionalServiceRequestsByServiceId({ commit }, id) {
      return await firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("serviceId", "==", id)
        .get()
        .then(querySnapshot => {
          const resList: Array<unknown> = [];

          querySnapshot.docs.forEach(x => {
            const thisRes = x.data();
            thisRes.id = x.id;
            thisRes.createdDateTime = moment(
              thisRes.createdDateTime.seconds * 1000
            ).toDate();
            resList.push(thisRes);
          });
          commit("setServiceRequestsByServiceId", resList);
          return resList
        })
    },

    async getAdditionalServiceRequestsByDates({ commit }, { start = moment(), end = moment() }) {
      const serviceRequests = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("createdDateTime", ">=", moment(start).toDate())
        .where("createdDateTime", "<", moment(end).add(1, "day").toDate());

      const result = await serviceRequests.get().then(querySnapshot => {
        return querySnapshot.docs.map(x => ({
          id: x.id,
          ...x.data(),
          createdDateTime: moment(
            x.data().createdDateTime.seconds * 1000
          ).toDate()
        }));
      });

      commit("setServiceRequests", result);
    },

    async getAdditionalServicesByDates(
      { commit },
      { start = moment(), end = moment() }
    ) {
      const services = firebase
        .firestore()
        .collection("additionalServices")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("createdDateTime", ">=", moment(start).toDate())
        .where(
          "createdDateTime",
          "<",
          moment(end)
            .add(1, "day")
            .toDate()
        );

      const result = await services.get().then(querySnapshot => {
        return querySnapshot.docs.map(x => ({
          id: x.id,
          ...x.data(),
          createdDateTime: x.data().createdDateTime.seconds * 1000
        }));
      });

      console.log({ result });

      commit("setAdditionalServices", result);
    },

    async getAdditionalServiceRequestsByResident(
      { commit },
      { residentId, statusList }
    ) {
      const calenderEvents = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("residentId", "==", residentId);

      if (statusList && statusList.length) {
        calenderEvents.where("status", "in", statusList);
      }

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.createdDateTime = moment(
            thisRes.createdDateTime.seconds * 1000
          ).toDate();

          resList.push(thisRes);
        });
        commit("setCurrentResidentServiceRequests", resList);
      });
    },

    async createAdditionServiceRequest({ dispatch }, payload) {

      payload.imageUrl = payload.imageUrl ? payload.imageUrl : require("@/assets/no-image.jpg");
      payload.thumbnailUrl = payload.thumbnailUrl ? payload.thumbnailUrl : require("@/assets/no-image.jpg");

      if (payload.serviceType == ServiceTypeEnum.Free) {
        payload.status = ServiceStatus.Approved;
      }

      payload.createdOnApp = 'StaffHub'
      payload.dateTimeCreated = firebase.firestore.Timestamp.fromDate(new Date())

      return await firebase
        .firestore()
        .collection("serviceRequests")
        .add(payload)
        .then(async(snap) => {
           await dispatch("getServiceRequests")
           await dispatch("getAdditionalServiceRequests", [])
           return snap.id
        })
        .catch(e => {
          console.error(e)
          return null
        });
    },

    async rejectAdditionalServiceRequest({ dispatch }, payload) {
      const rejectedByStaffName = this.state.authModule.user.name;

      await firebase
        .firestore()
        .collection("serviceRequests")
        .doc(payload.id)
        .update({
          status: "Rejected",
          rejectReason: payload.rejectReason,
          rejectComment: payload.rejectComment,
          isRejectedByStaff: true,
          rejectedByStaffName,
          rejectedDateTime: firebase.firestore.Timestamp.fromDate(new Date())
        })
        .then(() => {
          const id = this.state.additionalServicesModule.serviceRequest.id;
          dispatch("getAdditionalServiceRequest", id);
        });

      if (payload.approvalService) {
        await firebase
          .firestore()
          .collection("additionalServiceRequestApprovals")
          .doc(payload.approvalService.id)
          .update({
            done: true,
          }).then(() => {
            dispatch("getAdditionalServiceApprovals", payload.id)
          });
      }
    },

    async getAdditionalServiceEmail({ commit }) {
      firebase
        .firestore()
        .collection("emailTemplates")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", "Service Approval")
        .get()
        .then(querySnapshot => {
          const emailTemplate = querySnapshot.docs.map(emailTemplate => {
            const response = emailTemplate.data();
            response.id = emailTemplate.id;
            return {
              ...response
            };
          });
          commit("setEmailTemplate", emailTemplate);
        });
    },

    async sendAdditionalServiceEmail({ dispatch }, payload) {

      if (!payload.facilityId) {
        payload.facilityId = this.state.authModule.currentFacility
      }

      firebase
        .firestore()
        .collection("notifications")
        .add(payload)
        .then(() => {
          dispatch("getAdditionalServiceRequests", []);
        })
        .catch(e => console.error(e));
    },

    async updateAdditionalServiceRequestApprove({ dispatch }, payload) {
      return await firebase
        .firestore()
        .collection("additionalServiceRequestApprovals")
        .doc(payload.id)
        .update({
          approved: true,
          done: true,
          approvedDatetime: firebase.firestore.Timestamp.fromDate(new Date()),
          isStaffApproved: true,
          approvedByStaffEmail: this.state.authModule.user.email,
          approvedByStaffName: this.state.authModule.user.name
        })
        .then(async () => {
          return await firebase
            .firestore()
            .collection("additionalServiceRequestApprovals")
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .where("serviceRequestId", "==", payload.serviceRequestId)
            .get()
            .then(qs => {
              const isAllEPOAApprovedRequest = qs.docs.every(doc => {
                const data = doc.data();
                return data.approved && data.done;
              });

              const id = this.state.additionalServicesModule.serviceRequest.id;

              if (isAllEPOAApprovedRequest) {
                firebase
                  .firestore()
                  .collection("serviceRequests")
                  .doc(this.state.additionalServicesModule.serviceRequest.id)
                  .update({ status: ServiceStatus.Approved })
                  .then(() => {
                    dispatch("getAdditionalServiceRequest", id);
                    dispatch("getAdditionalServiceApprovals", id);
                  });
              } else {
                dispatch("getAdditionalServiceRequest", id);
                dispatch("getAdditionalServiceApprovals", id);
              }
            });
        })
        .catch(e => console.error(e));
    },

    async getAwaitingApprovalRequests({ commit }) {
      const serviceRequests = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("status", "==", ServiceStatus.AwaitingApproval);

      await serviceRequests.get().then(querySnapshot => {
        const resList: Array<unknown> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.createdDateTime = moment(
            thisRes.createdDateTime.seconds * 1000
          ).toDate();
          resList.push(thisRes);
        });

        commit("setServiceRequestsDashboard", resList);
      });
    },

    async getResidentServices({ commit }, { residentId, duration }) {
      const fromDate = moment()
        .add(-duration, "days")
        .toDate();

      const serviceRequests = firebase
        .firestore()
        .collection("serviceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("residentId", "==", residentId)
        .where("createdDateTime", ">=", moment(fromDate).toDate())
        .where("status", "in", [
          ServiceStatus.Completed,
          ServiceStatus.Approved,
          ServiceStatus.AwaitingApproval
        ]);

      await serviceRequests.get().then(querySnapshot => {
        const resList: Array<unknown> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(
            thisRes.createdDateTime.seconds * 1000
          ).toDate();
          resList.push(thisRes);
        });

        commit("setResidentServices", resList);
      });
    },

    async getVendors({ commit }) {
      const query = firebase
        .firestore()
        .collection("vendors")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      await query.get().then(querySnapshot => {
        const svss: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const ds = x.data();
          ds.id = x.id;
          svss.push(ds);
        });
        commit("setVendors", svss);
      });
    },

    async addVendor({ dispatch }, vendor) {
      vendor.facilityId = this.state.authModule.currentFacility;
      vendor.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
      vendor.email = vendor.email.trim().toLowerCase()

      return await firebase
        .firestore()
        .collection("vendors")
        .add(vendor)
        .then(async () => {
          return await dispatch("getVendors")
        });
    },

    async updateVendor({ dispatch }, vendor) {
      if (vendor.facilityId != this.state.authModule.currentFacility) throw Error('Unable to update vendor')

      vendor.updatedDateTime = firebase.firestore.Timestamp.fromDate(new Date())
      vendor.email = vendor.email.trim().toLowerCase()

      return await firebase
        .firestore()
        .collection("vendors")
        .doc(vendor.id)
        .update(vendor)
        .then(async () => {
          await dispatch("updateVendorsForTheScheduledMaintenance", vendor);
          await dispatch("updateVendorsForTheRequestsMaintenance", vendor);
          return await dispatch("getVendors")
        });
    },

    async updateVendorsForTheScheduledMaintenance(_, vendor) {
      await firebase
        .firestore()
        .collection("maintenanceScheduled")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("vendorId", "==", vendor.id)
        .get()
        .then(async (querySnapshot) => {
          const maintenanceScheduled = querySnapshot.docs.map(x => ({ ref: x.ref }))

          for(const maintenance of maintenanceScheduled) {
            await maintenance.ref.update({
              vendor: vendor.name
            })
          }
        });
    },

    async updateVendorsForTheRequestsMaintenance(_, vendor) {
      await firebase
        .firestore()
        .collection("maintenanceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("vendorId", "==", vendor.id)
        .get()
        .then(async (querySnapshot) => {
          const maintenanceScheduled = querySnapshot.docs.map(x => ({ ref: x.ref }))

          for(const maintenance of maintenanceScheduled) {
            await maintenance.ref.update({
              vendor: vendor.name
            })
          }
        });
    },

    async deleteVendor({ dispatch }, id) {
      const vendor = await firebase.firestore().collection("vendors").doc(id).get()
      if (vendor.data().facilityId != this.state.authModule.currentFacility) throw Error('Unable to delete vendor')

      return await vendor.ref.delete().then(async () => {
        return await dispatch("getVendors")
      });
    },
  }
};

export default additionalServicesModule;
